/// <amd-module name="Core/Medius.Core.Web/Scripts/components/taskHandling/shared/modals/rejectManagementWizard/rejectManagementEmailWizard"/>
import { Checkbox, FlexLayout, ResponsiveGridLayout, Switch, TextArea, Text, TextField, IconStatusErrorRegular, Button, ButtonGroup } from "@medius/ui-controls";  
import { translate } from "Core/Medius.Core.Web/Scripts/lib/globalization";
import * as React from "react";
import { IEmailSettings, IEmailContent } from "./interfaces";
import { getEmailSettings, getRejectionEmailAiContent } from "./services";
import { Control, Controller, ControllerRenderProps, FieldValues, FormState, UseFormClearErrors, UseFormHandleSubmit, UseFormReset, UseFormWatch, UseFormSetValue } from "react-hook-form";
import { RejectManagementEmailWizardFormState } from "./useRejectManagementEmailForm";
import { EmailFileAttachment } from "./emailAttachments/emailFileAttachment";

type RejectManagementEmailContext = {
    handleSubmit: UseFormHandleSubmit<FieldValues>;
    control: Control<FieldValues>;
    reset: UseFormReset<FieldValues>;
    defaultState: RejectManagementEmailWizardFormState;
    formState: FormState<FieldValues>;
    watch: UseFormWatch<FieldValues>;
    clearErrors: UseFormClearErrors<FieldValues>;
    setValue?: UseFormSetValue<FieldValues>;
};

type RejectManagementEmailWizardProps = {
    formContext?: RejectManagementEmailContext;
    documentType: string;
    documentId: number;
};

const defaultLanguageCode = "en-US";

export const RejectManagementEmailWizard = ({
    formContext,
    documentType,
    documentId,
}: RejectManagementEmailWizardProps) => {
    const { control, watch, clearErrors, setValue } = formContext;
    const sendToSupplier = watch("sendToSupplier");
    const [emailSettings, setEmailSettings] = React.useState<IEmailSettings | null>(null);
    const [selectedLanguage, setSelectedLanguage] = React.useState<string>(defaultLanguageCode);
    const [emailContentCache, setEmailContentCache] = React.useState<Record<string, IEmailContent>>({});
    const [userHasEmail, setUserHasEmail] = React.useState<boolean>(true);

    React.useEffect(() => {
        const fetchEmailContent = async (documentId: number, languageCode: string) => {
            return await getRejectionEmailAiContent(documentId, languageCode);
        };

        if (selectedLanguage && documentId) {
            const cacheKey = `${documentId}-${selectedLanguage}`;
            
            if (!emailContentCache[cacheKey]) {
                fetchEmailContent(documentId, selectedLanguage).then(content => {
                    if (content) {
                        setEmailContentCache(prev => ({
                            ...prev,
                            [cacheKey]: content
                        }));
                        
                        if (setValue) {
                            setValue("content", content.body || "", { shouldValidate: false });
                            setValue("messageSubject", content.subject || "", { shouldValidate: false });
                        }
                    }
                });
            } else {
                const cachedContent = emailContentCache[cacheKey];

                if (setValue && cachedContent) {
                    setValue("content", cachedContent.body || "", { shouldValidate: false });
                    setValue("messageSubject", cachedContent.subject || "", { shouldValidate: false });
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId, selectedLanguage, setValue]);

    const currentEmailContent = React.useMemo(() => {
        if (selectedLanguage && documentId) {
            const cacheKey = `${documentId}-${selectedLanguage}`;
            return emailContentCache[cacheKey];
        }
        return null;
    }, [selectedLanguage, documentId, emailContentCache]);

    const updateCachedContent = React.useCallback((field: 'subject' | 'body', value: string) => {
        if (selectedLanguage && documentId) {
            const cacheKey = `${documentId}-${selectedLanguage}`;
            const currentCache = emailContentCache[cacheKey];
        
            if (currentCache) {
                setEmailContentCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        ...currentCache,
                        [field]: value
                    }
                }));
            } else {
                setEmailContentCache(prev => ({
                    ...prev,
                    [cacheKey]: {
                        subject: field === 'subject' ? value : '',
                        body: field === 'body' ? value : ''
                    }
                }));
            }
        }
    }, [selectedLanguage, documentId, emailContentCache]);

    React.useEffect(() => {
        if (currentEmailContent && setValue) {
            const currentContentValue = watch("content");
            const currentSubjectValue = watch("messageSubject");
            
            if (!currentContentValue) {
                setValue("content", currentEmailContent.body || "", { shouldValidate: false });
            }
            
            if (!currentSubjectValue) {
                setValue("messageSubject", currentEmailContent.subject || "", { shouldValidate: false });
            }
        }
    }, [currentEmailContent, setValue, watch]);
    
    const stableFormContext = React.useMemo(() => ({
        setValue,
    }), [setValue]);

    React.useEffect(() => {
        const fetchEmailSettings = async () => {
            const settings = await getEmailSettings();
            setEmailSettings(settings);
            
            const hasSupplierLanguage = settings?.supplierLanguageCode && settings.supplierLanguageCode !== defaultLanguageCode;
            if (hasSupplierLanguage) {
                setSelectedLanguage(settings.supplierLanguageCode);
            } else {
                setSelectedLanguage(defaultLanguageCode);
            }
            if (!settings.currentUserEmail?.trim()) {
                setUserHasEmail(false);
                setValue('sendCopy', false);
            }
        };
        fetchEmailSettings();
    }, [setValue]);

    const handleSendToSupplierChange = (e: any, field: ControllerRenderProps) => {
        if(e.value === false)
            clearErrors();
        field.onChange(e.value);
    };

    const EmailLanguageButtons = () => {
        if (!emailSettings) return null;

        const userLanguageCode = emailSettings.currentUserLanguageCode;
        const hasSupplierLanguage = emailSettings.supplierLanguageCode  && emailSettings.supplierLanguageCode !== defaultLanguageCode;
        const buttons: { code: string; label: string; testId: string; key: string }[] = [];

        if (hasSupplierLanguage) {
            buttons.push({
                code: emailSettings.supplierLanguageCode!,
                label: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/buttonSupplierLanguage"),
                key: `${documentId}-${emailSettings.supplierLanguageCode!}`,
                testId: "supplier-language-button"
            });
        }

        if (emailSettings.currentUserLanguageCode !== defaultLanguageCode) {
            buttons.push({
                code: userLanguageCode,
                label: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/buttonMyLanguage"),
                key: "my-language-button",
                testId: "my-language-button",
            });
        } 

        buttons.push({
            code: defaultLanguageCode,
            label: translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/buttonEnglish"),
            key: "english-language-butto",
            testId: "english-language-button"
        });
        

        return (
            <ButtonGroup layout="row">
                {buttons.map(({ code, label, testId, key }) => (
                    <Button
                        key={key}
                        disabled={!sendToSupplier}
                        variant={selectedLanguage === code ? "primary" : "secondary"}
                        onClick={() => setSelectedLanguage(code)}
                        data-testid={testId}
                        label={label}
                    />
                ))}
            </ButtonGroup>
        );
    };


    return (
        <ResponsiveGridLayout 
            maxColumns={1}
            columnWidth={7} 
            >
            <Controller 
                name="emailTo"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        isRequired
                        disabled={!sendToSupplier}
                        fieldLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/emailTo")}
                        errorMessages={fieldState.error && sendToSupplier ? [fieldState.error.message || ""] : []}
                        inputProps={{
                            onChange:(e) => field.onChange(e.target.value),
                            value: field.value
                        }}
                        data-testid="reject-management-wizard-email-to"
                    />
                )}
            />
            <Controller 
                name="sendCopy"
                control={control}
                render={({ field }) => (
                    <Checkbox
                        disabled={!sendToSupplier || !userHasEmail}
                        label={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendMeACopy")}
                        onChange={(e) => field.onChange(e.target.value)}
                        value={field.value}
                        data-testid="reject-management-wizard-send-copy-checkbox"
                    />
                )}
            />

            {EmailLanguageButtons()}
            
            <Controller 
                name="messageSubject"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    <TextField
                        isRequired
                        disabled={!sendToSupplier}
                        fieldLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/messageSubject")}
                        errorMessages={fieldState.error ? [fieldState.error.message || ""] : []}
                        inputProps={{
                            onChange:(e) => {
                                const newValue = e.target.value;
                                field.onChange(newValue);
                                updateCachedContent('subject', newValue);
                            },
                            value: currentEmailContent?.subject || ""
                        }}
                        data-testid="reject-management-wizard-message-subject"
                    />
                )}
            />
            <Controller 
                name="content"
                control={control}
                rules={{
                    validate: (value) => !sendToSupplier ? true : value ? true : translate("#Core/defaultValidation_REQUIREDATTRIBUTE")
                }}
                render={({ field, fieldState }) => (
                    // TextAreaField has unremovable label and TextArea does not have default error handling so created it manually
                    <FlexLayout direction="column" spacerMode="off" itemRowSpacing="vsmall">
                        <TextArea
                            width="fill"
                            height="vlarge"
                            onChange={(e) => {
                                const newValue = e.target.value;
                                field.onChange(newValue);
                                updateCachedContent('body', newValue);
                            }}
                            value={ currentEmailContent?.body || ""}
                            disabled={!sendToSupplier}
                            invalid={!!fieldState.error}
                            data-testid="reject-management-wizard-email-content"
                        />
                        {
                            fieldState.error && (
                            <FlexLayout direction="row" itemColumnSpacing="vsmall">
                                <IconStatusErrorRegular color="error"/>
                                <Text variant="small" feedbackType="error">{ fieldState.error.message}</Text>
                            </FlexLayout>
                        )}
                    </FlexLayout>
                )}
            />
            <EmailFileAttachment
                documentType={documentType}
                documentId={documentId}
                formContext={stableFormContext}
            />
            <Controller 
                name="sendToSupplier"
                control={control}
                render={({ field }) => (
                    <Switch
                        leftLabel={translate("#PurchaseToPay/documentReasonCodes/rejectManagementWizard/sendEmailToSupplier")}
                        value={!!field.value}
                        onChange={(e) => handleSendToSupplierChange(e, field)}
                        data-testid="reject-management-wizard-send-to-supplier-switch"
                    />
                )}
            />
        </ResponsiveGridLayout>
    );
};